import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { IconButton, makeStyles } from '@material-ui/core'
import { DataGrid } from '@material-ui/data-grid'
import EditIcon from '@material-ui/icons/Edit'
import clsx from 'clsx'

import ResizeHandle from './ResizeHandle'
import { getDefaultColumns } from './columns'
import Settings from './Settings'
import Storage from '../../../../utils/Storage'
import { StopModal } from '../QuickLook'
import { getHasRequestPending } from '../../../../redux/data/selectors'
import Loader from '../../../../common/components/notifications/Loader'
import { GET_ENCODERS, GET_EVENTS } from '../../../../redux/data/types'

const useStyles = makeStyles((theme) => ({
  dataGridContainer: {
    width: '100%',
    position: 'relative',
    '&>div': {
      fontSize: '15px',
    },
    '& .MuiDataGrid-columnHeader': {
      outline: 'none !important',
      '-moz-user-select': 'none',
      '-khtml-user-select': 'none',
      '-webkit-user-select': 'none',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
      visibility: 'hidden',
    }
  },
  editCell: {
    width: '50px',
    margin: 'auto',
    padding: '0px !important',
    paddingLeft: '5px !important',
  },
  settingContainer: {
    position: 'absolute',
    right: 0,
    zIndex: '200',
    backgroundColor: '#fff1',
    border: '1px solid #ccc',
    borderRadius: '5px',
    margin: '5px',
    '& > button': {
      padding: '6px',
      color: 'black',
    },
  },
  dataGridRow: {
    '& > div:first-child': {
      padding: '0px'
    },
  },
  table: {
    display: 'flex',
    backgroundColor: 'white',
    padding: '1rem',
    marginBottom: '2rem',
    borderRadius: '7px',
  },

  editColumn: { display: 'flex', flexDirection: 'column', marginRight: '-1px' },
  editColumnBorder: { border: '1px solid #e0e0e0' },
  editColumnIcon: { borderRadius: 0, height: '100%', width: '50px' },
}))

const EVENT_TABLE_VIEW_COLUMNS_KEY = 'event-table-view-columns'
const EVENT_TABLE_VIEW_COLUMN_ORDERING_KEY = 'event-table-view-column-ordering'

const getDefaultColumnOrdering = (columns, storedColumnOrdering) => {
  if (storedColumnOrdering) {
    // need to ensure that all columns that aren't hidden are included in the ordering
    let ordering = [...storedColumnOrdering]
    Object.values(columns).forEach((col) => {
      if (col.hide !== true && !storedColumnOrdering.includes(col.field)) {
        ordering.push(col.field)
      }
    })
    return ordering
  } else {
    return Object.values(columns).map((col) => {
      return col.field
    })
  }
}

const EditEventButton = props => {
  const { eventID, rowHeight, toggleActiveEvent } = props
  const classes = useStyles()
  const centeringStyles = {display: 'flex', alignItems:'center', justifyContent: 'center'}
  return (
    <div
      className={classes.editColumnBorder}
      key={`edit button for ${eventID}`}
      style={{
        height: rowHeight,
        ...centeringStyles,
      }}
    >
      <IconButton
        title={eventID}
        className={classes.editColumnIcon}
        onClick={() => toggleActiveEvent(eventID)}
      >
        <EditIcon />
      </IconButton>
    </div>
  )
}

const getEventEditColumnHeader = (ROW_HEIGHT, toggleActiveEvent, headerClass) => {
  return {
    field: 'eventEdit',
    headerName: 'Edit',
    width: 50,
    sortable:false,
    filterable: false,
    renderHeader: () => {
      return <div style={{ height: ROW_HEIGHT }}>Edit</div>
    },
    headerClassName: headerClass,
    renderCell: params => {
      const eventID = params.id
      return (
        <EditEventButton
          eventID={eventID}
          toggleActiveEvent={toggleActiveEvent}
          rowHeight={ROW_HEIGHT}
        />
      )
    },
    hide: false
  }
}

const TableView = (props) => {
  const {
    events,
    dateDescending,
    resultMax = 10,
    storedColumns,
    storedColumnOrdering,
    toggleActiveEvent,
    actions,
    eventsRequestPending
  } = props

  const [columns, setColumns] = useState(
    storedColumns || Object.values(getDefaultColumns())
  )
  const [columnOrdering, setColumnOrdering] = useState(
    getDefaultColumnOrdering(columns, storedColumnOrdering)
  )
  const [showStopModal, setShowStopModal] = useState(false)
  const [sortModel, setSortModel] = useState([
    {
      field: 'startDateTime',
      sort: dateDescending ? 'desc' : 'asc',
    },
  ])

  useEffect(() => {
    setSortModel([
      {
        field: 'startDateTime',
        sort: dateDescending ? 'desc' : 'asc',
      }
    ])
  }, [dateDescending])

  const classes = useStyles()
  
  //#region event actions
  const handleRecord = (eventID) => {
    actions.start({
      eventID: eventID,
    })
  }

  const handlePause = (event) => {
    var newStatus = event.eventStatus === 'live' ? 3 : 2
    var liveStats = event.liveStats

    actions.edit({
      eventID: event.eventID,
      liveID: liveStats.liveID,
      eventStatus: newStatus,
    })
  }

  const confirmStop = (ev) => {
    actions.stop({
      eventID: showStopModal,
    })
    setShowStopModal(false)
  }

  const hideStopModal = (ev) => {
    setShowStopModal(false)
  }

  const handleStop = (eventID) => {
    setShowStopModal(eventID)
  }
  
// #endregion

  const rows = Object.values(events).map((event) => {
    return {
      ...event,
      id: event.eventID, //needed for datagrid component
      eventActions: {
        pause: () => handlePause(event),
        stop: () => handleStop(event.eventID),
        record: () => handleRecord(event.eventID),
      },
    }
  })

  const handleSetColumnOrdering = (newOrdering) => {
    Storage.set(EVENT_TABLE_VIEW_COLUMN_ORDERING_KEY, newOrdering)
    setColumnOrdering(newOrdering)
  }

  const handleSetColumns = (newColumns) => {
    Storage.set(EVENT_TABLE_VIEW_COLUMNS_KEY, newColumns)
    setColumns(newColumns)
  }

  const handleColUpdate = (colIndex, newColumn) => {
    const newColumns = [...columns]
    newColumns[colIndex] = { ...newColumns[colIndex], ...newColumn }
    handleSetColumns(newColumns)
  }

  const handleColWidthChange = (colIndex, newWidth) => {
    const newColumns = [...columns]
    newColumns.find((col) => col.field === columnOrdering[colIndex]).width =
      newWidth
    handleSetColumns(newColumns)
  }

  const handleUpdateAllHidden = (shouldHide) => {
    const newColumns = [...columns].map((col) => ({ ...col, hide: shouldHide }))
    handleSetColumns(newColumns)
  }

  const resetSettings = () => {
    handleSetColumns(Object.values(getDefaultColumns()))
    handleSetColumnOrdering(
      getDefaultColumnOrdering(Object.values(getDefaultColumns()))
    )
  }

  const ROW_HEIGHT = 75
  const HEADER_HEIGHT = 55 + 2 // (2 extra for 1px border top and bottom)
  const SCROLL_BUFFER_HEIGHT = 18 // space needed for the horizontal scroll bar below the list

  if (!columns) {
    return <div>there was a problem with columns</div>
  }
  const noEvents = Object.values(events).length === 0
  const eventEditColumn = !(noEvents || eventsRequestPending) ? (
    getEventEditColumnHeader(ROW_HEIGHT, toggleActiveEvent, classes.editCell)
  ) : (
    <div></div>
  )
  return (
    <div
      className={classes.table}
    >
      <StopModal
        show={!!showStopModal}
        event={events[showStopModal]}
        onHide={hideStopModal}
        onConfirm={confirmStop}
      />
      <div
        className={clsx(classes.dataGridContainer, 'event-table')}
        style={{
          height:
            rows.length * ROW_HEIGHT + HEADER_HEIGHT + SCROLL_BUFFER_HEIGHT,
        }}
      >
        <div className={classes.settingContainer}>
          {noEvents ? null : (
            <Settings
              columns={columns}
              updateColumn={handleColUpdate}
              setAllHiddenState={handleUpdateAllHidden}
              columnOrdering={columnOrdering}
              setColumnOrdering={handleSetColumnOrdering}
              resetSettings={resetSettings}
            />
          )}
        </div>
        {eventsRequestPending ? <Loader /> : null}
        <DataGrid
          rowHeight={ROW_HEIGHT}
          showCellRightBorder={false}
          sortModel={sortModel}
          rows={eventsRequestPending ? [] : rows}
          columns={[eventEditColumn, ...columnOrdering.map((columnField, i) => {
            const column = columns.find((col) => col.field === columnField)
            const col = {
              ...column,
              sortable: false,
              filterable: false,
              renderHeader: (params) => {
                if (noEvents || eventsRequestPending) {
                  return <div></div>
                }
                // add the resize handlers
                return (
                  <div style={{ width: '100%' }}>
                    <div
                      style={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'pre',
                        width: '100%',
                      }}
                    >
                      {column.headerName}
                    </div>
                    <ResizeHandle
                      colWidth={column.width}
                      onSizeChange={(width) => handleColWidthChange(i, width)}
                    />
                  </div>
                )
              },
            }
            return col
          })]}
          hideFooter={true}
          components={{
            NoRowsOverlay:
              noEvents && !eventsRequestPending
                ? NoRowsComponent
                : EmptyComponent,
          }}
          getRowClassName={(id, field) => {
            return classes.dataGridRow
          }}
          disableColumnMenu={true}
          pageSize={Math.min(resultMax, 100, Object.keys(events).length)}
        />
      </div>
    </div>
  )
}

const EmptyComponent = props => {
  // the passed in component has to be a component (not null)
  return <div></div>
}

const NoRowsComponent = props => {
  return <div style={{ margin: 'auto' }}>No Events Found</div>
}


const mapStateToProps = (state) => {
  const dateDescending = state.events.filters.sortBy === 'DESC'
  const resultMax = state.events.filters.resultMax

  const pendingRequestTypes = [GET_EVENTS, GET_ENCODERS]
  const eventsRequestPending = getHasRequestPending(state, pendingRequestTypes)

  const storedColumns = Storage.get(EVENT_TABLE_VIEW_COLUMNS_KEY)
  if (storedColumns) {
    // replace stored render cell functions with most current version
    const defaultColumns = getDefaultColumns()
    storedColumns.forEach((col) => {
      col.renderCell = defaultColumns[col.field].renderCell
    })
  }
  const storedColumnOrdering = Storage.get(EVENT_TABLE_VIEW_COLUMN_ORDERING_KEY)

  return {
    dateDescending,
    resultMax,
    storedColumns,
    storedColumnOrdering,
    eventsRequestPending,
  }
}

export default connect(mapStateToProps)(TableView)
